import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BriefcaseBusiness,
  CalendarDays,
  Check,
  ChevronRight,
  CircleHelp,
  Clock3,
  Compass,
  FileText,
  Globe2,
  MapPin,
  Pencil,
  RefreshCw,
  Trash2,
  UserRound,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabase";
import { isMissingTravelTimeColumn } from "../lib/bookingErrors";
import { MOCK_GUIDES } from "../data/guides";
import BusLoading from "../components/BusLoading";
import LanguageSwitcher from "../components/LanguageSwitcher";
import ProfileAvatar from "../components/ProfileAvatar";

const ACTIVE_STATUSES = new Set(["new", "accepted", "in_progress"]);

function ActionRow({ to, icon: Icon, title, description }) {
  return (
    <Link
      to={to}
      className="group flex min-h-[4.5rem] items-center gap-3 rounded-2xl px-0 py-3 transition duration-200 hover:bg-slate-50 hover:shadow-md hover:shadow-slate-900/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 dark:hover:bg-slate-800/70 dark:hover:shadow-black/25 sm:gap-4"
    >
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-amber-50 text-amber-600 transition group-hover:bg-amber-400 group-hover:text-slate-950 dark:bg-amber-400/10 dark:text-amber-400 dark:group-hover:bg-amber-400">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-bold text-slate-900 dark:text-white sm:text-base">{title}</span>
        <span className="mt-0.5 block text-xs leading-5 text-slate-500 dark:text-slate-400">{description}</span>
      </span>
      <ChevronRight className="h-5 w-5 shrink-0 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-amber-500" aria-hidden="true" />
    </Link>
  );
}

export default function ClientDashboardPage() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [guidesBySlug, setGuidesBySlug] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [actionError, setActionError] = useState("");
  const [filter, setFilter] = useState("active");
  const [updatingId, setUpdatingId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deletingAccount, setDeletingAccount] = useState(false);

  const loadBookings = useCallback(async ({ silent = false } = {}) => {
    if (!supabase || !user?.id) {
      setLoadError(true);
      setBookings([]);
      setLoading(false);
      return;
    }

    if (!silent) {
      setLoading(true);
      setLoadError(false);
    }
    try {
      let [bookingResult, guideResult] = await Promise.all([
        supabase.from("bookings").select("id, guide_slug, travel_date, travel_time, message, status, created_at").order("travel_date", { ascending: true }),
        supabase.rpc("get_public_guides"),
      ]);
      if (bookingResult.error && isMissingTravelTimeColumn(bookingResult.error)) {
        bookingResult = await supabase
          .from("bookings")
          .select("id, guide_slug, travel_date, message, status, created_at")
          .order("travel_date", { ascending: true });
        if (!bookingResult.error) {
          bookingResult = {
            ...bookingResult,
            data: (bookingResult.data || []).map((booking) => ({ ...booking, travel_time: null })),
          };
        }
      }
      if (bookingResult.error) {
        if (!silent) {
          setLoadError(true);
          setBookings([]);
        }
      } else {
        setLoadError(false);
        setBookings(bookingResult.data || []);
      }
      setGuidesBySlug(Object.fromEntries((guideResult.data || []).map((guide) => [guide.guide_slug, guide.full_name])));
    } catch {
      if (!silent) {
        setLoadError(true);
        setBookings([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadBookings();
    const intervalId = window.setInterval(() => loadBookings({ silent: true }), 10000);
    const refreshOnFocus = () => loadBookings({ silent: true });
    window.addEventListener("focus", refreshOnFocus);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshOnFocus);
    };
  }, [loadBookings]);

  const activeCount = bookings.filter((booking) => ACTIVE_STATUSES.has(booking.status)).length;
  const filteredBookings = useMemo(() => bookings.filter((booking) => {
    const active = ACTIVE_STATUSES.has(booking.status);
    return filter === "all" || (filter === "active" ? active : !active);
  }), [bookings, filter]);

  const cancelBooking = async (id) => {
    setUpdatingId(id);
    setActionError("");
    try {
      const { error } = await supabase.from("bookings").update({ status: "cancelled" }).eq("id", id);
      if (error) throw error;
      await loadBookings();
    } catch {
      setActionError(t("dashboard.client_cancel_error"));
    } finally {
      setUpdatingId(null);
    }
  };

  const deleteConfirmationWord = t("dashboard.client_delete_confirm_word");
  const canConfirmAccountDeletion = deleteConfirmation.trim().toLocaleLowerCase() === deleteConfirmationWord.toLocaleLowerCase();

  const handleDeleteAccount = async (event) => {
    event.preventDefault();
    if (!supabase || !canConfirmAccountDeletion || deletingAccount) return;

    setDeletingAccount(true);
    setDeleteError("");
    try {
      const { data, error } = await supabase.functions.invoke("delete-account", { body: {} });
      let response = data;
      if (error && !response) {
        try {
          response = await error.context?.json();
        } catch {
          // Use the translated fallback if the Edge Function has no JSON body.
        }
      }
      if (error || !response?.success) {
        const status = error?.context?.status || error?.status;
        const isUnavailable = status === 404
          || error?.name === "FunctionsFetchError"
          || error?.name === "FunctionsRelayError";
        const errorKey = response?.errorKey
          || (isUnavailable ? "dashboard.client_delete_service_unavailable" : "dashboard.client_delete_error");
        throw new Error(errorKey);
      }

      await logout();
      navigate("/", { replace: true });
    } catch (error) {
      setDeleteError(t(error?.message || "dashboard.client_delete_error"));
    } finally {
      setDeletingAccount(false);
    }
  };

  const formatDate = (date) => new Intl.DateTimeFormat(i18n.language, {
    day: "numeric", month: "long", year: "numeric",
  }).format(new Date(`${date}T00:00:00`));
  const formatTime = (time) => time?.slice(0, 5) || t("booking.time_pending");

  const getGuideName = (slug) => {
    const guide = MOCK_GUIDES.find((item) => item.id === slug);
    return guidesBySlug[slug] || (guide && t(`guides_data.${guide.id}.name`, guide.name)) || t("dashboard.client_guide_fallback");
  };

  const tabs = [
    { id: "active", label: t("dashboard.client_filter_active") },
    { id: "all", label: t("dashboard.client_filter_all") },
    { id: "history", label: t("dashboard.client_filter_history") },
  ];
  const emptyKey = filter === "active" ? "dashboard.client_empty_active" : filter === "history" ? "dashboard.client_empty_history" : "dashboard.client_empty_all";

  return (
    <main className="min-h-[calc(100svh-68px)] bg-[#F7F6F3] px-3 pb-28 pt-5 text-slate-900 dark:bg-slate-950 dark:text-slate-100 sm:px-6 sm:pb-14 sm:pt-10 md:px-10">
      <div className="mx-auto max-w-6xl">
        <section className="relative overflow-hidden rounded-3xl border border-slate-800 bg-[#111722] p-5 text-white shadow-xl shadow-slate-950/10 sm:rounded-[2rem] sm:p-8 lg:p-10">
          <div className="pointer-events-none absolute -right-16 -top-24 h-64 w-64 rounded-full border border-amber-300/10 sm:h-80 sm:w-80" />
          <div className="pointer-events-none absolute -right-4 -top-12 h-48 w-48 rounded-full border border-amber-300/10 sm:h-64 sm:w-64" />
          <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:gap-6">
            <ProfileAvatar avatarUrl={user.avatar_url} name={user.name} className="h-20 w-20 border-4 border-slate-700 shadow-lg sm:h-24 sm:w-24" textClassName="text-2xl sm:text-3xl" />
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-extrabold uppercase tracking-[.2em] text-amber-300 sm:text-xs">{t("dashboard.client_badge")}</p>
              <h1 className="mt-2 break-words text-2xl font-black tracking-tight sm:text-3xl lg:text-4xl">{user.name}</h1>
              <p className="mt-1 break-all text-sm text-slate-300">{user.email}</p>
            </div>
            <div className="flex w-full flex-wrap gap-2 sm:w-auto sm:justify-end">
              <Link to="/profile" aria-label={t("dashboard.client_edit_profile")} title={t("dashboard.client_edit_profile")} className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-400 text-slate-950 transition hover:bg-amber-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#111722]">
                <Pencil className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>

        <section aria-label={t("dashboard.client_summary")} className="mt-4 grid grid-cols-2 gap-3 sm:mt-5 sm:gap-4">
          <article className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:rounded-3xl sm:p-6">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-600 dark:bg-amber-400/10 dark:text-amber-400"><FileText className="h-5 w-5" aria-hidden="true" /></span>
            <p className="mt-3 text-2xl font-black sm:text-3xl">{loading ? "—" : bookings.length}</p>
            <p className="mt-0.5 text-xs font-semibold text-slate-500 dark:text-slate-400 sm:text-sm">{t("dashboard.client_stat_total")}</p>
          </article>
          <article className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:rounded-3xl sm:p-6">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 dark:bg-emerald-400/10 dark:text-emerald-400"><CalendarDays className="h-5 w-5" aria-hidden="true" /></span>
            <p className="mt-3 text-2xl font-black sm:text-3xl">{loading ? "—" : activeCount}</p>
            <p className="mt-0.5 text-xs font-semibold text-slate-500 dark:text-slate-400 sm:text-sm">{t("dashboard.client_stat_active")}</p>
          </article>
        </section>

        <div className="mt-4 grid items-stretch gap-4 lg:mt-5 lg:grid-cols-[minmax(0,1.45fr)_minmax(17rem,.8fr)] lg:gap-5">
          <section className="min-w-0 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:rounded-3xl sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-extrabold uppercase tracking-[.18em] text-amber-600 dark:text-amber-400">{t("dashboard.client_trips_eyebrow")}</p>
                <h2 className="mt-1 text-xl font-black sm:text-2xl">{t("dashboard.client_trips_title")}</h2>
              </div>
              <button type="button" onClick={loadBookings} disabled={loading} aria-label={t("dashboard.client_refresh")} className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:border-amber-400 hover:text-amber-600 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300">
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
              </button>
            </div>

            <div className="mt-4 flex max-w-full gap-2 overflow-x-auto pb-1" role="tablist" aria-label={t("dashboard.client_filter_label")}>
              {tabs.map((tab) => <button key={tab.id} type="button" role="tab" aria-selected={filter === tab.id} onClick={() => setFilter(tab.id)} className={`shrink-0 rounded-full px-4 py-2 text-xs font-bold transition sm:text-sm ${filter === tab.id ? "bg-amber-400 text-slate-950" : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"}`}>{tab.label}</button>)}
            </div>

            {actionError && <p role="alert" className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-400/20 dark:bg-red-400/10 dark:text-red-200">{actionError}</p>}
            {loadError && <div role="alert" className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-400/20 dark:bg-amber-400/10 dark:text-amber-100"><p>{t("dashboard.client_load_error")}</p><button type="button" onClick={loadBookings} className="mt-2 font-bold underline underline-offset-2">{t("dashboard.client_retry")}</button></div>}
            {loading ? <div className="py-10"><BusLoading compact title={t("dashboard.loading")} /></div> : !loadError && filteredBookings.length === 0 ? (
              <div className="mt-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center dark:border-slate-700 dark:bg-slate-800/50 sm:px-6">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-50 text-amber-600 dark:bg-amber-400/10 dark:text-amber-400"><Compass className="h-6 w-6" aria-hidden="true" /></span>
                <h3 className="mt-3 font-bold">{t(emptyKey)}</h3>
                <Link to="/guides" className="mt-4 inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-amber-400 px-5 py-2.5 text-sm font-extrabold text-slate-950 hover:bg-amber-300">{t("dashboard.client_find_guide")}<ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
              </div>
            ) : !loading && !loadError ? (
              <div className="mt-4 space-y-3">
                {filteredBookings.map((booking) => {
                  const canCancel = ["new", "accepted"].includes(booking.status);
                  const StatusIcon = booking.status === "completed" ? Check : CalendarDays;
                  return (
                    <article key={booking.id} className="rounded-2xl border border-slate-100 bg-white p-4 dark:border-slate-800 dark:bg-slate-900 sm:p-5">
                      <div className="flex items-start gap-3">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600 dark:bg-amber-400/10 dark:text-amber-400"><StatusIcon className="h-5 w-5" aria-hidden="true" /></span>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                            <h3 className="break-words font-bold">{getGuideName(booking.guide_slug)}</h3>
                            <span className="text-xs font-bold text-amber-700 dark:text-amber-300">{t(`booking.status.${booking.status}`, booking.status)}</span>
                          </div>
                          <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400"><CalendarDays className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />{formatDate(booking.travel_date)}</p>
                          <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400"><Clock3 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />{formatTime(booking.travel_time)}</p>
                          {booking.status === "accepted" && <p role="status" aria-live="polite" className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3.5 py-3 text-sm font-semibold leading-6 text-emerald-900 dark:border-emerald-400/20 dark:bg-emerald-400/10 dark:text-emerald-100">{booking.travel_time ? t("booking.notice_accepted") : t("booking.notice_accepted_time_pending")}</p>}
                          {booking.status === "in_progress" && <p role="status" aria-live="polite" className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3.5 py-3 text-sm font-semibold text-emerald-900 dark:border-emerald-400/20 dark:bg-emerald-400/10 dark:text-emerald-100">{t("booking.notice_in_progress")}</p>}
                          {booking.message && <p className="mt-3 whitespace-pre-wrap break-words text-sm leading-6 text-slate-600 dark:text-slate-300">{booking.message}</p>}
                          {canCancel && <button type="button" onClick={() => cancelBooking(booking.id)} disabled={updatingId === booking.id} className="mt-3 rounded-full border border-red-200 px-3.5 py-2 text-xs font-bold text-red-600 transition hover:bg-red-50 disabled:opacity-50 dark:border-red-400/20 dark:text-red-300 dark:hover:bg-red-400/10">{updatingId === booking.id ? t("dashboard.client_cancelling") : t("dashboard.cancel")}</button>}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : null}
          </section>

          <aside className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:rounded-3xl sm:p-6">
            <h2 className="pb-2 text-[10px] font-extrabold uppercase tracking-[.18em] text-slate-400 dark:text-slate-500">{t("dashboard.client_account_title")}</h2>
            <ActionRow to="/dashboard/ride-history" icon={FileText} title={t("dashboard.client_order_history")} description={t("dashboard.client_order_history_hint")} />
            <ActionRow to="/work" icon={BriefcaseBusiness} title={t("work_portal.account_action")} description={t("work_portal.account_action_hint")} />
            <ActionRow to="/profile" icon={UserRound} title={t("dashboard.client_profile_action")} description={t("dashboard.client_profile_hint")} />
            <ActionRow to="/cities" icon={MapPin} title={t("dashboard.client_regions_action")} description={t("dashboard.client_regions_hint")} />
            <ActionRow to="/contact" icon={CircleHelp} title={t("dashboard.client_support_action")} description={t("dashboard.client_support_hint")} />
            <div className="flex min-h-[4.5rem] items-center gap-3 rounded-2xl px-0 py-3 sm:gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-amber-50 text-amber-600 dark:bg-amber-400/10 dark:text-amber-400"><Globe2 className="h-5 w-5" aria-hidden="true" /></span>
              <span className="min-w-0 flex-1 text-sm font-bold text-slate-900 dark:text-white">{t("dashboard.client_language")}</span>
              <LanguageSwitcher />
            </div>
            <div className="mt-auto border-t border-slate-200 pt-4 dark:border-slate-800 sm:pt-5">
              <button
                type="button"
                onClick={() => {
                  setDeleteError("");
                  setDeleteConfirmation("");
                  setDeleteDialogOpen(true);
                }}
                className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-red-200 px-4 py-2.5 text-sm font-bold text-red-600 transition hover:border-red-300 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 dark:border-red-400/25 dark:text-red-300 dark:hover:border-red-400/50 dark:hover:bg-red-400/10"
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                {t("dashboard.client_delete_account")}
              </button>
            </div>
          </aside>
        </div>
      </div>

      {deleteDialogOpen && (
        <div className="mobile-viewport-overlay fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/75 px-4 py-6 backdrop-blur-sm" onMouseDown={(event) => {
          if (event.target === event.currentTarget && !deletingAccount) setDeleteDialogOpen(false);
        }}>
          <section role="alertdialog" aria-modal="true" aria-labelledby="delete-account-title" aria-describedby="delete-account-description" className="mobile-viewport-panel w-full max-w-lg rounded-3xl border border-slate-200 bg-white p-5 shadow-2xl dark:border-slate-700 dark:bg-[#111214] sm:p-7">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-red-600 dark:bg-red-400/10 dark:text-red-300">
              <Trash2 className="h-5 w-5" aria-hidden="true" />
            </div>
            <h2 id="delete-account-title" className="mt-4 text-xl font-black text-slate-950 dark:text-white sm:text-2xl">{t("dashboard.client_delete_title")}</h2>
            <p id="delete-account-description" className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">{t("dashboard.client_delete_description")}</p>

            <form onSubmit={handleDeleteAccount}>
              <label className="mt-5 block">
                <span className="mb-2 block text-sm font-bold text-slate-800 dark:text-slate-100">{t("dashboard.client_delete_type_prompt", { word: deleteConfirmationWord })}</span>
                <input
                  autoFocus
                  value={deleteConfirmation}
                  onChange={(event) => setDeleteConfirmation(event.target.value)}
                  disabled={deletingAccount}
                  autoComplete="off"
                  className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-red-400 focus:ring-2 focus:ring-red-400/20 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                />
              </label>

              {deleteError && <p role="alert" className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm leading-5 text-red-700 dark:border-red-400/20 dark:bg-red-400/10 dark:text-red-200">{deleteError}</p>}

              <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button type="button" disabled={deletingAccount} onClick={() => setDeleteDialogOpen(false)} className="min-h-11 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800">
                  {t("dashboard.client_delete_cancel")}
                </button>
                <button type="submit" disabled={!canConfirmAccountDeletion || deletingAccount} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-red-600 px-5 py-2.5 text-sm font-extrabold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-45">
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                  {deletingAccount ? t("dashboard.client_delete_loading") : t("dashboard.client_delete_confirm")}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
    </main>
  );
}
